import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV } from "@/lib/site";
import { useContent, buildWa } from "@/lib/ContentProvider";

export default function Navbar() {
  const { siteConfig } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-[60] transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-alch-bg/80 border-b border-alch-forest/10 py-3" : "py-5"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            {siteConfig.logo ? (
              <img src={siteConfig.logo} alt="ALCH" className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <span className="grid h-10 w-10 place-items-center rounded-full bg-alch-forest text-alch-bg font-serif text-lg font-semibold">A</span>
            )}
            <span className="leading-none">
              <span className="block font-serif text-xl font-semibold text-alch-forest">ALCH</span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-alch-muted">Laser Care Hospital</span>
            </span>
          </Link>

          <div className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`link-underline text-sm font-medium transition-colors ${
                  pathname === n.to ? "text-alch-terracotta" : "text-alch-ink hover:text-alch-forest"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={`tel:${siteConfig.phoneRaw}`}
              data-testid="nav-call"
              className="hidden items-center gap-2 text-sm font-medium text-alch-forest md:flex"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> {siteConfig.phone}
            </a>
            <a
              href={buildWa(siteConfig.whatsapp)}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-book-appointment"
              className="hidden rounded-full bg-alch-forest px-5 py-2.5 text-sm font-medium text-alch-bg transition-transform duration-300 hover:scale-[1.04] sm:inline-block"
            >
              Book Appointment
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="grid h-11 w-11 place-items-center rounded-full border border-alch-forest/20 text-alch-forest lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-alch-forest px-6 pt-28 pb-10 text-alch-bg lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  <Link to={n.to} className="block border-b border-alch-bg/15 py-4 font-serif text-3xl">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <a
                href={buildWa(siteConfig.whatsapp)}
                target="_blank"
                rel="noreferrer"
                className="mt-6 rounded-full bg-alch-terracotta px-6 py-4 text-center text-base font-medium"
              >
                Book on WhatsApp
              </a>
              <a href={`tel:${siteConfig.phoneRaw}`} className="mt-2 text-center text-sm text-alch-bg/70">
                {siteConfig.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
